import { useState } from 'react'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import copy from 'copy-to-clipboard'
import QRCode from 'qrcode-svg'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Cognito from 'modules/personal_settings/store/models/Cognito'

import ActivationHelp from './_ActivationHelp'
import DeactivationHelp from './_DeactivationHelp'

interface Props {
  onClose: () => void
  onSubmit: (mfaCode: string) => void
  mfaEnabled: boolean
}

const MFAModal = ({ onClose, onSubmit, mfaEnabled }: Props) => {
  const [qrCodeSVG, setQrCode] = useState<string>()
  const [mfaSetupCode, setMfaSetupCode] = useState<string>()
  const [mfaCode, setMfaCode] = useState<string>('')
  const cognitoAPI = new Cognito({})

  const modalTitle = mfaEnabled
    ? i18n.t('personal_settings.mfa.deactivate_modal_title')
    : i18n.t('personal_settings.mfa.activate_modal_title')

  const modalDescription = mfaEnabled
    ? i18n.t('personal_settings.mfa.deactivate_modal_description')
    : i18n.t('personal_settings.mfa.activate_modal_description')

  const submitButtonText = mfaEnabled
    ? i18n.t('personal_settings.mfa.deactivate')
    : i18n.t('personal_settings.mfa.activate')

  const submitButtonType = mfaEnabled ? 'negative' : 'primary'

  const { loading } = useAsyncData(async () => {
    if (mfaEnabled) {
      return Promise.resolve({
        loading: true,
      })
    }

    const result = await asyncAction(() => cognitoAPI.fetchMfaSecretCode(), { successFlash: false })

    if (isSuccessful(result)) {
      const { email, mfa_secret_code } = result.value

      const qrCodeObject = new QRCode({
        content: `otpauth://totp/Factorial:${email}?secret=${mfa_secret_code}&issuer=Factorial`,
        join: true,
        container: 'svg-viewbox',
        padding: 8,
      })
      setQrCode(qrCodeObject.svg().trim())
      setMfaSetupCode(mfa_secret_code)
    }
  }, [])

  const onQrCodeClick = () => {
    if (!mfaSetupCode) return

    copy(mfaSetupCode)
    app.setFlash({
      type: 'correct',
      message: i18n.t('common.copied'),
    })
  }

  const onChange = (value: string) => {
    setMfaCode(value)
  }

  return (
    <Modal
      illustration="user-edit"
      onClose={onClose}
      onSubmit={() => onSubmit(mfaCode)}
      title={modalTitle}
      description={modalDescription}
      footer={
        <>
          <Button type="secondary" disabled={loading} onClick={onClose}>
            {i18n.t('personal_settings.mfa.cancel')}
          </Button>
          <Button disabled={loading || !mfaCode} type={submitButtonType} submit>
            {submitButtonText}
          </Button>
        </>
      }
    >
      {!mfaEnabled && (
        <>
          <ActivationHelp />
          <Box height="s240" alignItems="center" justifyContent="center">
            {loading && <Spinner />}
            {qrCodeSVG && (
              <Box height="s240" width="s240" onClick={onQrCodeClick}>
                <img src={`data:image/svg+xml;utf8,${encodeURIComponent(qrCodeSVG)}`} />
              </Box>
            )}
          </Box>
        </>
      )}
      {mfaEnabled && <DeactivationHelp />}
      <Box paddingTop="s12">
        <TextField
          label={i18n.t('personal_settings.mfa.verification_code')}
          placeholder={i18n.t('personal_settings.mfa.two_factor_auth_code')}
          onChange={onChange}
        />
      </Box>
    </Modal>
  )
}

export default observer(MFAModal)
